import React, { useEffect, useMemo, useState } from 'react';
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Trash2, Loader2 } from "lucide-react";
import { MatchedPairData, JournalLine, reconciliationApi } from "../lib/api";
import { generateReconciliationPairs } from "../lib/reconciliation";
import { useToast } from "../hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "./ui/tooltip";
import { formatDisplayDateTime } from "../lib/dateUtils";

interface MatchedPairCardProps {
  pair: MatchedPairData;
  isSelected: boolean;
  isUnmatching?: boolean;
  onSelect: (selected: boolean) => void;
  onUnmatch: () => void;
  onCommit?: (selection: { debitLines: JournalLine[]; creditLines: JournalLine[] }) => void;
  isCommitting?: boolean;
  readOnly?: boolean;
}

const cloneLines = (lines: JournalLine[] = []): JournalLine[] =>
  lines.map((line) => ({ ...line }));

const parseAmount = (value?: string | number): number => {
  if (value === undefined || value === null) return 0;
  if (typeof value === "number") return value;
  const numeric = parseFloat(value.replace(/[^0-9.-]/g, ""));
  return Number.isFinite(numeric) ? numeric : 0;
};

export const MatchedPairCard: React.FC<MatchedPairCardProps> = ({
  pair,
  isSelected,
  isUnmatching = false,
  onSelect,
  onUnmatch,
  onCommit,
  isCommitting = false,
  readOnly = false,
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [debitLines, setDebitLines] = useState<JournalLine[]>(() => cloneLines(pair.preview.debit_lines || []));
  const [creditLines, setCreditLines] = useState<JournalLine[]>(() => cloneLines(pair.preview.credit_lines || []));
  const { toast } = useToast();

  // Initialize amount from the first debit line or transaction amount
  const [amount, setAmount] = useState<string>(
    pair.preview.debit_lines?.[0]?.amount || pair.transaction.amount
  );

  // Update local amount state when lines change (e.g. from props)
  useEffect(() => {
    const currentAmount = pair.preview.debit_lines?.[0]?.amount || pair.transaction.amount;
    // Only update if significantly different to avoid cursor jumping if we were bi-directional binding
    // But this is effect from props, so it should be fine.
    if (currentAmount !== amount && !readOnly) {
      setAmount(currentAmount);
    }
  }, [pair.preview.debit_lines, pair.transaction.amount]);

  const handleAmountChange = (newAmount: string) => {
    setAmount(newAmount);

    // Update local lines immediately for UI responsiveness
    const newDebitLines = debitLines.map(line => ({ ...line, amount: newAmount }));
    const newCreditLines = creditLines.map(line => ({ ...line, amount: newAmount }));

    setDebitLines(newDebitLines);
    setCreditLines(newCreditLines);

    // Note: onUpdate is not available in props, so we rely on persistSelection (onBlur) 
    // to update backend, and local state for UI responsiveness.
  };

  const handleAmountBlur = () => {
    if (readOnly) return;

    const newDebitLines = debitLines.map(line => ({ ...line, amount: amount }));
    const newCreditLines = creditLines.map(line => ({ ...line, amount: amount }));

    persistSelection(newDebitLines, newCreditLines);
  };

  const persistSelection = async (
    nextDebit: JournalLine[],
    nextCredit: JournalLine[],
    overrides?: { debitAccount?: string; creditAccount?: string }
  ): Promise<boolean> => {
    if (readOnly) {
      return true;
    }

    try {
      const recLines = generateReconciliationPairs(nextDebit, nextCredit);

      await reconciliationApi.updateLinkAccounts(pair.link_id, {
        debit_account: overrides?.debitAccount,
        credit_account: overrides?.creditAccount,
        debit_lines: nextDebit,
        credit_lines: nextCredit,
        reconciliation_lines: recLines
      });
      return true;
    } catch (error) {
      toast({
        title: "Failed to save selection",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
      return false;
    }
  };

  useEffect(() => {
    setDebitLines(cloneLines(pair.preview.debit_lines || []));
    setCreditLines(cloneLines(pair.preview.credit_lines || []));
  }, [pair.link_id, pair.preview.debit_lines, pair.preview.credit_lines]);

  const previewPending = Boolean(pair.preview.pending_selection);
  const pendingMessage =
    pair.preview.pending_message || "Accounts will appear shortly. Refresh in a minute.";
  const availableDebitAccounts = pair.preview.available_debit_accounts || [];
  const availableBankAccounts = pair.preview.available_bank_accounts || [];



  const accountOptions = useMemo(() => {
    const optionMap = new Map<string, { code: string; name: string; type?: string }>();
    [...availableDebitAccounts, ...availableBankAccounts].forEach((acc) => {
      if (!optionMap.has(acc.code)) {
        optionMap.set(acc.code, { code: acc.code, name: acc.name, type: acc.type });
      }
    });

    [...debitLines, ...creditLines].forEach((line) => {
      if (line.account && !optionMap.has(line.account)) {
        optionMap.set(line.account, {
          code: line.account,
          name: line.description || line.account,
          type: line.account.startsWith("5") ? "bank" : "other",
        });
      }
    });

    return Array.from(optionMap.values()).sort((a, b) => a.code.localeCompare(b.code));
  }, [pair.preview.available_debit_accounts, pair.preview.available_bank_accounts, debitLines, creditLines]);



  const updateDebitAccount = (index: number, account: string) => {
    const option = accountOptions.find((opt) => opt.code === account);
    const previousDebit = cloneLines(debitLines);
    const creditSnapshot = cloneLines(creditLines);
    const nextDebit = cloneLines(debitLines);
    nextDebit[index] = {
      ...nextDebit[index],
      account,
      description: option?.name ?? nextDebit[index]?.description ?? account,
    };

    setDebitLines(nextDebit);

    void (async () => {
      const success = await persistSelection(nextDebit, creditSnapshot, {
        debitAccount: index === 0 ? account : undefined,
      });
      if (!success) {
        setDebitLines(previousDebit);
      }
    })();
  };

  const updateCreditAccount = (index: number, account: string) => {
    const option = accountOptions.find((opt) => opt.code === account);
    const previousCredit = cloneLines(creditLines);
    const debitSnapshot = cloneLines(debitLines);
    const nextCredit = cloneLines(creditLines);
    nextCredit[index] = {
      ...nextCredit[index],
      account,
      description: option?.name ?? nextCredit[index]?.description ?? account,
    };

    setCreditLines(nextCredit);

    void (async () => {
      const success = await persistSelection(debitSnapshot, nextCredit, {
        creditAccount: index === 0 ? account : undefined,
      });
      if (!success) {
        setCreditLines(previousCredit);
      }
    })();
  };



  const handleRemoveDebitLine = (index: number) => {
    if (readOnly) return;
    const previousDebit = cloneLines(debitLines);
    const creditSnapshot = cloneLines(creditLines);
    const nextDebit = debitLines.filter((_, idx) => idx !== index);
    setDebitLines(nextDebit);

    void (async () => {
      const success = await persistSelection(nextDebit, creditSnapshot, {
        debitAccount: nextDebit[0]?.account,
      });
      if (!success) {
        setDebitLines(previousDebit);
      }
    })();
  };

  const handleRemoveCreditLine = (index: number) => {
    if (readOnly) return;
    const previousCredit = cloneLines(creditLines);
    const debitSnapshot = cloneLines(debitLines);
    const nextCredit = creditLines.filter((_, idx) => idx !== index);
    setCreditLines(nextCredit);

    void (async () => {
      const success = await persistSelection(debitSnapshot, nextCredit, {
        creditAccount: nextCredit[0]?.account,
      });
      if (!success) {
        setCreditLines(previousCredit);
      }
    })();
  };

  const totalDebit = debitLines.reduce((sum, line) => sum + parseAmount(line.amount), 0);
  const totalCredit = creditLines.reduce((sum, line) => sum + parseAmount(line.amount), 0);
  const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01;

  const handleConfirm = () => {
    setConfirmOpen(false);
    onUnmatch();
  };

  const handleCommit = () => {
    if (readOnly || !onCommit) return;
    onCommit({ debitLines, creditLines });
  };

  const renderAccountSelect = (
    line: JournalLine,
    index: number,
    onChange: (value: string) => void,
    onRemove: () => void,
    showApx: boolean = false, // Only show APX on debit side
  ) => {
    // Check if line has APX data (counterparty info) AND we should show it
    const hasApxData = showApx && (line.client_code || line.invoice_number || line.invoice_date);

    return (
      <div className="flex flex-col gap-1 rounded" key={index}>
        <div className="flex items-center gap-2">
          <Select value={line.account || ""} onValueChange={onChange} disabled={readOnly}>
            <SelectTrigger className="flex-1 h-8" disabled={readOnly}>
              <SelectValue placeholder="Select account" />
            </SelectTrigger>
            <SelectContent>
              {accountOptions.map((acc) => (
                <SelectItem key={acc.code} value={acc.code}>
                  {acc.code} - {acc.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {!readOnly && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-gray-400"
              onClick={onRemove}
              aria-label="Remove line"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
        {/* APX Fields - Show on debit side only when counterparty data exists */}
        {hasApxData && (
          <div className="ml-2 pl-2 border-l-2 border-blue-200 text-xs text-gray-600 space-y-0.5">
            {line.client_code && (
              <div className="flex gap-2">
                <span className="text-gray-400 w-16">Supplier:</span>
                <span className="font-medium">
                  {line.client_code}{pair.invoice?.vendor ? ` - ${pair.invoice.vendor}` : ''}
                </span>
              </div>
            )}
            {line.invoice_number && (
              <div className="flex gap-2">
                <span className="text-gray-400 w-16">Invoice:</span>
                <span className="font-mono">{line.invoice_number}</span>
              </div>
            )}
            {line.invoice_date && (
              <div className="flex gap-2">
                <span className="text-gray-400 w-16">Date:</span>
                <span>{line.invoice_date}</span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-gray-100 border-2 border-[#0000FF] rounded-2xl p-3 relative">

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex justify-between items-center mb-4 pb-3 border-b">
              <h4 className="font-semibold">Transaction {pair.transaction.reference}</h4>
              <Badge
                variant="secondary"
                className="border bg-blue-100 border-[#0000FF] text-[#0000FF] font-light"
              >
                {pair.is_committed ? 'Committed' : 'Matched'}
              </Badge>
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Amount:</span>
                <span className="font-mono font-semibold">{pair.transaction.amount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Date:</span>
                <span>{pair.transaction.date}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">From:</span>
                <span className="font-medium">{pair.transaction.from}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">To:</span>
                <span className="font-medium">{pair.transaction.to}</span>
              </div>
              <div className="flex justify-between items-start gap-4">
                <span className="text-gray-600 whitespace-nowrap">Purpose:</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-right truncate flex-1 cursor-default">
                      {pair.transaction.purpose}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs break-words">
                    <p>{pair.transaction.purpose}</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex justify-between items-center mb-4 pb-3 border-b">
              <h4 className="font-semibold">
                {pair.invoice.mirage_invoice_number ? `Mirage Invoice: ${pair.invoice.mirage_invoice_number}` : pair.invoice.filename}
              </h4>
              <Badge
                variant="secondary"
                className="border bg-blue-100 border-[#0000FF] text-[#0000FF] font-light"
              >
                {pair.is_committed ? 'Committed' : 'Matched'}
              </Badge>
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Amount:</span>
                <span className="font-mono font-semibold">{pair.invoice.amount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Invoice Date:</span>
                <span>{pair.invoice.invoice_date}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Customer:</span>
                <span className="text-right">{pair.invoice.customer}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Vendor:</span>
                <span className="text-right">{pair.invoice.vendor}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Type:</span>
                <span>{pair.invoice.type}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div>
        {previewPending ? (
          <div className="border border-dashed border-blue-200 bg-white rounded-lg p-4 flex items-center gap-3">
            <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
            <div className="text-sm text-slate-600">
              <p className="font-medium text-slate-800">Generating account suggestions…</p>
              <p className="text-xs text-slate-500">{pendingMessage}</p>
            </div>
          </div>
        ) : (
          <>


            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
              <div>
                <h5 className="font-medium mb-1">Debit</h5>
                {pair.preview.suggested_debit_reasoning && (
                  <p className="text-xs text-slate-500 mb-2">
                    Suggestion: {pair.preview.debit_lines?.[0]?.account || ""} — {pair.preview.suggested_debit_reasoning}
                    {pair.preview.suggested_debit_confidence ? ` (confidence: ${pair.preview.suggested_debit_confidence})` : ""}
                  </p>
                )}
                <div className="space-y-2">
                  {debitLines.map((line, index) =>
                    renderAccountSelect(line, index, (value) => updateDebitAccount(index, value), () => handleRemoveDebitLine(index), true)
                  )}
                </div>
              </div>

              <div>
                <h5 className="font-medium mb-1">Credit</h5>
                {pair.preview.suggested_bank_reasoning && (
                  <p className="text-xs text-slate-500 mb-2">Bank inference: {pair.preview.suggested_bank_reasoning}</p>
                )}
                <div className="space-y-2">
                  {creditLines.map((line, index) =>
                    renderAccountSelect(line, index, (value) => updateCreditAccount(index, value), () => handleRemoveCreditLine(index), false)
                  )}
                </div>
              </div>
            </div>

            {/* Single Editable Amount Field */}
            <div className="flex items-center gap-2 mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200 justify-center">
              <span className="font-medium text-gray-700">Reconciliation Amount:</span>
              <Input
                value={amount}
                onChange={(e) => handleAmountChange(e.target.value)}
                onBlur={handleAmountBlur}
                className="w-32 font-mono text-right bg-white"
                disabled={readOnly}
              />
              <span className="text-gray-500 font-medium">EUR</span>
            </div>

            {!isBalanced && !readOnly && (
              <p className="text-xs text-red-500 mt-2">Debit and credit totals must balance before committing.</p>
            )}
          </>
        )}

        <div className="flex items-center justify-between pt-4 border-t mt-4">
          <div className="text-sm text-gray-700">
            Doc#
            <div className="font-semibold">{
              (() => {
                const invoiceDate = new Date(pair.invoice.invoice_date);
                const month = Number.isNaN(invoiceDate.getTime()) ? 0 : invoiceDate.getMonth() + 1;
                return month.toString().padStart(10, '0');
              })()
            }</div>
          </div>
          <div className="text-sm text-black font-light">
            Match status <span className="font-bold">{pair.match_percentage}%</span>
          </div>
          {readOnly ? (
            <div className="text-sm text-gray-600 text-right">
              {pair.is_committed && pair.committed_at ? (
                <div>Committed at {formatDisplayDateTime(pair.committed_at)}</div>
              ) : (
                <div>Committed</div>
              )}
              {pair.mirage_accrec_id ? (
                <div className="text-xs text-gray-500">AccRec #{pair.mirage_accrec_id}</div>
              ) : null}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant={isSelected ? "default" : "outline"}
                size="sm"
                onClick={() => onSelect(!isSelected)}
                className={`w-20 ${isSelected ? "bg-blue-600 hover:bg-blue-700" : ""}`}
              >
                {isSelected ? "Selected" : "Select"}
              </Button>
              <Dialog
                open={confirmOpen}
                onOpenChange={(open) => {
                  if (isUnmatching) {
                    setConfirmOpen(false);
                    return;
                  }
                  setConfirmOpen(open);
                }}
              >
                <DialogTrigger asChild>
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    disabled={isUnmatching}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    {isUnmatching ? 'Unmatching...' : 'Unmatch'}
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Unmatch pair?</DialogTitle>
                    <DialogDescription>
                      This will remove the link between the transaction and invoice. You can re-match them later if needed.
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setConfirmOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="button" variant="destructive" onClick={handleConfirm}>
                      Confirm Unmatch
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              <Button
                type="button"
                variant="blue"
                size="sm"
                onClick={handleCommit}
                disabled={isCommitting || isUnmatching || debitLines.length === 0 || creditLines.length === 0}
              >
                {isCommitting ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Committing...
                  </span>
                ) : (
                  'Commit to accounting software'
                )}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
